/**
 * The mixing desk (05 §1): one AudioContext, three buses into a master, and a gentle compressor
 * so a scan of explosions never clips.
 *
 *   sfx ----------------------[sfx slider]---+
 *   ui -----------------------[sfx slider]---+--> master --> compressor --> out
 *   music --> duck (in-cave) --[music slider]-+
 */

import { heldNoise } from './rand';
import { sliderGain, type Volumes } from './types';

const RAMP = 0.05;
/** Headroom per bus, so the sliders at full still leave the compressor little to do. */
const TRIM = { sfx: 0.8, ui: 0.6, music: 0.55 } as const;
/** The noise generator's base rate, Hz: each random value is held for sampleRate / this samples. */
const NOISE_BASE = 4000;
const NOISE_SECONDS = 2;
/** How far a big effect pulls the music down, and for how long. */
const DUCK_DEPTH = 0.45;
const DUCK_HOLD = 0.25;

/** Whatever this browser calls it; null where there is no WebAudio (node, a locked-down webview). */
export function audioContextClass(): typeof AudioContext | null {
  const scope = globalThis as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext };
  return scope.AudioContext ?? scope.webkitAudioContext ?? null;
}

export class Bus {
  readonly ctx: AudioContext;
  readonly sfx: GainNode;
  readonly ui: GainNode;
  readonly music: GainNode;
  /** Held white noise, shared by every noise voice; `noiseHz` is its rate at playback rate 1. */
  readonly noise: AudioBuffer;
  readonly noiseHz: number;

  private readonly sfxSlider: GainNode;
  private readonly uiSlider: GainNode;
  private readonly musicSlider: GainNode;
  private readonly ducker: GainNode;
  private readonly master: GainNode;
  private resuming = false;

  constructor(Ctor: typeof AudioContext, volumes: Volumes) {
    const ctx = new Ctor({ latencyHint: 'interactive' });
    this.ctx = ctx;
    this.sfx = ctx.createGain();
    this.ui = ctx.createGain();
    this.music = ctx.createGain();
    this.sfxSlider = ctx.createGain();
    this.uiSlider = ctx.createGain();
    this.musicSlider = ctx.createGain();
    this.ducker = ctx.createGain();
    this.master = ctx.createGain();

    // Gentle: a soft knee a little below full scale, so it only leans on pile-ups.
    const comp = ctx.createDynamicsCompressor();
    comp.threshold.value = -14;
    comp.knee.value = 12;
    comp.ratio.value = 4;
    comp.attack.value = 0.004;
    comp.release.value = 0.25;

    this.sfx.connect(this.sfxSlider).connect(this.master);
    this.ui.connect(this.uiSlider).connect(this.master);
    this.music.connect(this.ducker).connect(this.musicSlider).connect(this.master);
    this.master.connect(comp).connect(ctx.destination);

    const hold = Math.max(1, Math.round(ctx.sampleRate / NOISE_BASE));
    this.noiseHz = ctx.sampleRate / hold;
    const white = heldNoise(Math.round(ctx.sampleRate * NOISE_SECONDS), hold, 0x51d);
    this.noise = ctx.createBuffer(1, white.length, ctx.sampleRate);
    this.noise.copyToChannel(white, 0);
    this.setVolumes(volumes);
  }

  get now(): number {
    return this.ctx.currentTime;
  }

  /** Sound can be heard, or will be in a moment (a resume is on its way). */
  get live(): boolean {
    return this.ctx.state === 'running' || this.resuming;
  }

  /** Browsers start a context suspended until a user gesture; calling this again later is harmless. */
  resume(): void {
    if (this.ctx.state === 'running' || this.resuming) return;
    this.resuming = true;
    this.ctx.resume().then(
      () => { this.resuming = false; },
      () => { this.resuming = false; },
    );
  }

  setVolumes(v: Volumes): void {
    const t = this.now;
    this.master.gain.setTargetAtTime(sliderGain(v.master), t, RAMP);
    this.sfxSlider.gain.setTargetAtTime(sliderGain(v.sfx) * TRIM.sfx, t, RAMP);
    this.uiSlider.gain.setTargetAtTime(sliderGain(v.sfx) * TRIM.ui, t, RAMP);
    this.musicSlider.gain.setTargetAtTime(sliderGain(v.music) * TRIM.music, t, RAMP);
  }

  /** A big effect: pull the music down at once and let it come back (the in-cave groove ducks). */
  duck(): void {
    const g = this.ducker.gain;
    const t = this.now;
    g.cancelScheduledValues(t);
    g.setTargetAtTime(1 - DUCK_DEPTH, t, 0.015);
    g.setTargetAtTime(1, t + DUCK_HOLD, 0.3);
  }
}
